import React from "react";
import { useNavigate } from "react-router-dom";
import { UseFormRegisterReturn } from "react-hook-form";
import { formatUSCurrency } from "utils/formatUSCurrency";

interface CoverageSelectFieldProps {
    register: UseFormRegisterReturn;
    error?: string;
    value?: string;
    isEditable?: boolean;
    disabled?: boolean;
    id?: string;
    fullWidth?: boolean;
    editNavigationPath?: string;
    coverageOptions: Array<{ coverageAmount: number; monthlyPremium: number }>;
}

const CoverageSelectField = ({
    register,
    error,
    value,
    isEditable = false,
    disabled = false,
    id,
    fullWidth = false,
    editNavigationPath,
    coverageOptions,
}: CoverageSelectFieldProps) => {
    // ========== Hooks ==========
    const navigate = useNavigate();

    // ========== Computed Values ==========
    const width = fullWidth ? "form-control w-full" : "form-control flex-1 min-w-0";

    // ========== Event Handlers ==========
    const handleEditClick = () => {
        if (editNavigationPath) {
            navigate(editNavigationPath);
            setTimeout(() => {
                document.getElementById("hero-card")?.scrollIntoView({ behavior: "auto" });
            }, 0);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        // Call the register's onChange first
        if (register.onChange) {
            register.onChange(e);
        }
        // Trigger validation immediately when a value is selected
        if (register.onBlur) {
            register.onBlur({ target: { name: register.name, value: e.target.value } } as any);
        }
    };

    // ========== Render ==========
    return (
        <div className={width}>
            {isEditable ? (
                <div className="flex justify-between items-center">
                    <label className="label h-7" htmlFor={id}>
                        <span className="text-base-content">Coverage</span>
                    </label>
                    <button onClick={handleEditClick} className="text-edit-link font-bold text-sm leading-5 hover:underline">
                        Edit
                    </button>
                </div>
            ) : (
                <label className="label h-7" htmlFor={id}>
                    <span className="text-base-content">Coverage</span>
                </label>
            )}
            <select
                {...register}
                onChange={handleChange}
                id={id}
                value={value}
                className={`select select-bordered bg-base-100 h-9 min-h-9 text-sm ${
                    disabled || (isEditable && !error) ? "opacity-50 cursor-not-allowed" : ""
                } ${error ? "select-error" : ""} ${!value ? "text-gray-400" : ""}`}
                disabled={disabled || isEditable}
            >
                <option value="" disabled>
                    Select
                </option>
                {coverageOptions.map((coverage) => (
                    <option key={coverage.coverageAmount} value={coverage.coverageAmount} className="text-base-content">
                        {formatUSCurrency(coverage.coverageAmount)} - {formatUSCurrency(coverage.monthlyPremium)}/month
                    </option>
                ))}
            </select>
            {error && <p className="text-error text-xs mt-1">{error}</p>}
        </div>
    );
};

export default CoverageSelectField;
